import { checkSession } from "@features/auth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@shadcn/ui/tabs";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";
import { Suspense, useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { AppHeader } from "~/components/layout/AppHeader";
import { HubMap } from "~/components/layout/HubMap/HubMap";
import { PageContainer } from "~/components/layout/PageContainer";
import { BackLink } from "~/components/one-delivery/BackLink";
import { OfferCard, PlanCard } from "~/components/one-delivery/CreditCards";
import { ONBOARDING_STEPS } from "~/components/one-delivery/onboarding";
import type { pricingOneDeliveryProfileSaveMutation } from "./__generated__/pricingOneDeliveryProfileSaveMutation.graphql";
import type { pricingOneDeliveryQuery } from "./__generated__/pricingOneDeliveryQuery.graphql";

const query = graphql`
	query pricingOneDeliveryQuery {
		creditOffers {
			id
			title
			description
			features
			credits
			priceCents
			maxPerUser
			purchasable
		}
		creditPlans {
			id
			title
			description
			features
			credits
			priceCents
		}
	}
`;

const saveMutation = graphql`
	mutation pricingOneDeliveryProfileSaveMutation($hubId: UUID!) {
		onedeliveryProfileSave(input: { preferredHubId: $hubId }) {
			userId
			baceId
		}
	}
`;

export const Route = createFileRoute("/one-delivery/onboarding/pricing")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery";
	},
	component: RouteComponent,
	loader: async ({ context }) => {
		if (!(await checkSession())) {
			throw redirect({ to: "/auth", search: { redirectToPath: "/one-delivery/onboarding/pricing", show: "sign-in" } });
		}
		return preloadQuery<pricingOneDeliveryQuery>(context.relayEnvironment, query, {}, { fetchPolicy: "network-only" });
	},
});

type Choice = { offerId: string } | { planId: string };

/** Figma "OD - Order Step 1 - Pricing" followed by "OD - Order Step 2 - Select Hub", with the offers and plans of the admins. */
function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const { creditOffers, creditPlans } = usePreloadedQuery<pricingOneDeliveryQuery>(query, Route.useLoaderData());
	const offers = creditOffers.filter((offer) => offer.purchasable);
	const [tab, setTab] = useState<"one-time" | "subscription">(offers.length === 0 && creditPlans.length > 0 ? "subscription" : "one-time");
	const [choice, setChoice] = useState<Choice | null>(null);
	const [save, isSaving] = useAsyncMutation<pricingOneDeliveryProfileSaveMutation>(saveMutation);

	const chooseHub = async (hubId: string, close: () => void) => {
		if (!choice) return;
		try {
			await save({ variables: { hubId } });
			close();
			await navigate({ to: "/one-delivery/onboarding/summary", search: choice });
		} catch (error) {
			toast.error(i18n.OD_ONB_HUB_SAVE_FAILED(), { description: error instanceof Error ? error.message : undefined });
		}
	};

	const select = (next: Choice) => (
		<Button className="h-9 w-full text-sm font-medium" onClick={() => setChoice(next)}>
			{i18n.OD_ONB_SELECT_PLAN()}
		</Button>
	);

	return (
		<PageContainer className="min-h-dvh bg-slate-50">
			<AppHeader />
			<BackLink to="/one-delivery/onboarding" search={{ step: ONBOARDING_STEPS }} />
			<div className="flex flex-col gap-10 px-6 pt-4 pb-16">
				<div className="flex flex-col gap-4 text-center">
					<h1 className="font-breit text-2xl font-normal text-neutral-950">
						<T>{i18n.OD_ONB_PRICING_TITLE()}</T>
					</h1>
					<p className="text-base text-neutral-500">{i18n.OD_ONB_PRICING_TEXT()}</p>
				</div>
				<div className="flex flex-col items-center gap-8">
					<Tabs value={tab} onValueChange={(value) => setTab(value as typeof tab)}>
						<TabsList className="h-9 rounded-[10px] bg-neutral-100 p-[3px]">
							<TabsTrigger value="one-time" className="px-2 py-1 font-sans text-sm font-medium">
								{i18n.OD_ONB_TAB_ONE_TIME()}
							</TabsTrigger>
							<TabsTrigger value="subscription" className="px-2 py-1 font-sans text-sm font-medium">
								{i18n.OD_ONB_TAB_SUBSCRIPTION()}
							</TabsTrigger>
						</TabsList>
					</Tabs>
					<div className="flex w-full flex-col gap-4">
						{tab === "one-time" && offers.map((offer) => <OfferCard key={offer.id} offer={offer} action={select({ offerId: offer.id })} />)}
						{tab === "subscription" &&
							creditPlans.map((plan) => <PlanCard key={plan.id} plan={plan} action={select({ planId: plan.id })} />)}
						{(tab === "one-time" ? offers : creditPlans).length === 0 && (
							<p className="text-center text-sm text-muted-foreground">{i18n.OD_ONB_NO_OFFERS()}</p>
						)}
					</div>
				</div>
			</div>
			{choice && (
				<Suspense fallback={null}>
					<HubMap
						open
						onOpenChange={(open) => !open && setChoice(null)}
						filterService="ONEDELIVERY"
						renderAction={({ id }, close) => (
							<Button className="w-full font-breit font-normal" disabled={isSaving} onClick={() => void chooseHub(id, close)}>
								{isSaving && <LoaderCircle className="size-4 animate-spin" />}
								{i18n.CHOOSE_HUB()}
							</Button>
						)}
					/>
				</Suspense>
			)}
		</PageContainer>
	);
}
